<template>
    <div class="episode-content base-card__content base-card__zoom base-card__shadow" :class="classesEpisodeContent">
        <div class="episode-content__wrapper">
            <img 
                v-if="imageUrl"
                loading="lazy"
                class="episode-content__image" 
                :class="{'program-content__image_blured': isParentallyRestricted}"
                alt=""
                :src="imageUrl"
            >
            <common-icon v-else class="episode-content__image" :icon-name="'IconNoPicture'" />

            <div v-show="isPlaying" class="episode-content__label-playing"> <label-playing /></div>
        </div>
        <common-icon v-if="isParentallyRestricted" class="episode-content__image-lock" :icon-name="'IconLock'" />
        <span v-if="isLive" class="episode-content__live">
            {{ live }}
        </span>
        <span class="episode-content__track">
            <span :style="progressStyles" />
        </span>
        <div class="base-card__shift episode-content__description">
            <div class="episode-content__name title">{{ title }}</div>
            <div class="episode-content__time">{{ duration }}</div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { type PropType, type ComputedRef, type StyleValue  } from "vue";
    import { PlayItem, type OnDemandItem } from "@ca.vmedia.tv/domains";
    import { Strings } from "@/locales/types/enums";
    import { type EpisodeCardData } from "@/components/Cards/types";
    import { useI18n } from "vue-i18n";
    import { useParentControl } from "@/composables/parentControl/useParentControl";
    import { computed } from "vue";
    import { usePlayerStore } from "@/stores/Player";
    import { storeToRefs } from 'pinia';

    const PlayerStoreModule = usePlayerStore();
    const {
        playProgram
    } = storeToRefs(PlayerStoreModule);

    const props = defineProps({
        cardData: {
            type: Object as PropType<EpisodeCardData>,
            required: true
        },
    });

    const { t } = useI18n();
    const { needsUnlockPin } = useParentControl()
    const live: string = t(Strings.HOME_LIVE).toString();

    const imageUrl = computed((): string => {
        const image = props.cardData.getImage();

        if (!image) {
            return '';
        } else {
            return `${image}&width=${448}`;
        }
    });
    const title = computed((): string => props.cardData.getName());
    const item = computed((): OnDemandItem => props.cardData.getPlayItem());
 
    const duration = computed((): string => {
        if (!item.value.lengthInS) {
            return "";
        }
        const hours = Math.floor(item.value.lengthInS / 3600);
        const minutes = Math.floor((item.value.lengthInS - (3600 * hours)) / 60);
        return `${hours}h ${minutes}min`;
    });
    const progress = computed((): number => {
        if (!item.value?.lengthInS || !props.cardData.getStreamPosition) {
            return 0;
        }
        return Math.floor((props.cardData.getStreamPosition() / item.value.lengthInS) * 100);
    });
    const progressStyles = computed((): StyleValue => {
        return {
            width: `${progress.value}%`,
        };
    });
    const isLive = computed((): boolean => PlayItem.isLive(item.value));
    const isParentallyRestricted = computed((): boolean => needsUnlockPin(item.value?.advisory));

    const isPlaying: ComputedRef<boolean> = computed(() => playProgram.value ? String(playProgram.value.id) === String(props.cardData.getId()) : false);
    const classesEpisodeContent: ComputedRef<{[key: string]: boolean}> = computed(() => {
        return {
            'episode-content_isPlaying': isPlaying.value
        };
    });
</script>

<style lang="scss">
    @import url("styles.scss");
</style>